import { orderTypeConstants } from "constants/order";

export type StepStatus = "complete" | "current" | "upcoming";

export const StepKey = {
  PATIENT: "patient",
  VISIT: "visit",
  BENEFITS: "benefits",
  SERVICES: "services",
  PAYMENT: "payment",
  COMMUNICATION: "communication",
} as const;

export type StepKeyValue = (typeof StepKey)[keyof typeof StepKey];

export function isFilled(value: unknown): boolean {
  if (value === null || value === undefined || value === false) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;

  return true;
}

export const BENEFIT_VALUE_FIELDS = [
  "deductible",
  "outOfPocketMax",
  "flatCopay",
  "coinsurancePercent",
];

export const SECONDARY_BENEFIT_VALUE_FIELDS = BENEFIT_VALUE_FIELDS.map(
  (field) => `${field}2`
);

type StepDefinition = {
  fields: string[];
  isComplete?: (values: Record<string, any>) => boolean;
  noRequiredInput?: boolean;
};

export const STEPS: Record<StepKeyValue, StepDefinition> = {
  [StepKey.PATIENT]: {
    fields: [
      "orderType",
      "firstName",
      "lastName",
      "dateOfBirth",
      "phoneNumber",
    ],
  },
  [StepKey.VISIT]: { fields: ["dateOfService", "accountNumber"] },
  [StepKey.BENEFITS]: {
    fields: [
      "payer",
      ...BENEFIT_VALUE_FIELDS,
      "secondaryInsuranceActivated",
      ...SECONDARY_BENEFIT_VALUE_FIELDS,
    ],
    isComplete: (values) => {
      const primaryFilled =
        isFilled(values.payer) &&
        BENEFIT_VALUE_FIELDS.every((field) => isFilled(values[field]));

      if (!primaryFilled) return false;
      if (!values.secondaryInsuranceActivated) return true;

      return SECONDARY_BENEFIT_VALUE_FIELDS.every((field) =>
        isFilled(values[field])
      );
    },
  },
  [StepKey.SERVICES]: { fields: ["serviceDetails"] },
  [StepKey.PAYMENT]: { fields: [], noRequiredInput: true },
  [StepKey.COMMUNICATION]: {
    fields: ["sendDefaultText", "customText"],
    isComplete: (values) =>
      values.sendDefaultText === true || isFilled(values.customText),
  },
};

export function visibleStepKeys(orderType?: string): StepKeyValue[] {
  const isGfe = orderType === orderTypeConstants.GFE;

  return [
    StepKey.PATIENT,
    StepKey.VISIT,
    ...(isGfe ? [StepKey.BENEFITS] : []),
    StepKey.SERVICES,
    StepKey.PAYMENT,
    StepKey.COMMUNICATION,
  ];
}

export function resolveCompleteness(
  keys: StepKeyValue[],
  values: Record<string, any>
): boolean[] {
  const completeness: boolean[] = [];

  keys.forEach((key) => {
    const definition = STEPS[key];
    const precedingComplete = completeness.every(Boolean);

    if (definition.noRequiredInput) {
      completeness.push(precedingComplete);
      return;
    }

    if (definition.isComplete) {
      completeness.push(definition.isComplete(values));
      return;
    }

    completeness.push(
      definition.fields.length > 0 &&
        definition.fields.every((field) => isFilled(values[field]))
    );
  });

  return completeness;
}

export function resolveStepStatus(
  step: number,
  completeness: boolean[]
): StepStatus {
  const index = step - 1;

  if (completeness[index]) return "complete";

  return completeness.slice(0, index).every(Boolean) ? "current" : "upcoming";
}
